// @ts-nocheck
/*
 * Call Groups list — v1 logic preserved (allocated-CLIs modal, bulk +
 * single delete, ACL gates on Call Group module, per-column truthy
 * filters). react-bootstrap chrome swapped for shadcn Card + Dialog +
 * ConfirmDialog + existing shared DataTable.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Pencil, Trash2, Search } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import Loader from '@/components/partials/components/Loader';
import {
  API_AGENT_GROUPS_LIST,
  API_AGENT_GROUP_DELETE,
  API_AGENT_GROUP_AVAILABLE_INBOUND_NUMBERS,
} from '@/service/url';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { getUser } from '@/utilities/setting';
import { USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Plus } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const AgentGroups = () => {
  const navigate = useNavigate();
  const [agentGroupList, setAgentGroupList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [selectedAgentGroups, setSelectedAgentGroups] = useState<any[]>([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [, setIsSuperadmin] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [showViewClisModal, setShowViewClisModal] = useState(false);
  const [viewClisGroup, setViewClisGroup] = useState<any>(null);
  const [viewClisList, setViewClisList] = useState<any[]>([]);
  const [viewClisLoading, setViewClisLoading] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState<any>({});

  const openViewClisModal = async (group) => {
    if (!group?.id) return;
    setViewClisGroup(group);
    setViewClisList([]);
    setShowViewClisModal(true);
    setViewClisLoading(true);
    try {
      const list = await getApiCall(API_AGENT_GROUP_AVAILABLE_INBOUND_NUMBERS.replace(':id', group.id));
      const allocated = (Array.isArray(list) ? list : []).filter(
        (n) => n.cli_target_type === 'QUEUE' && String(n.cli_target_id) === String(group.id),
      );
      setViewClisList(allocated);
    } catch {
      toast.error('Failed to fetch allocated CLIs');
      setViewClisList([]);
    } finally {
      setViewClisLoading(false);
    }
  };

  const fetchAgentGroups = () => {
    setLoading(true);
    getApiCall(API_AGENT_GROUPS_LIST)
      .then((resp: any) => {
        if (resp) {
          const updatedList = resp.map((val, idx) => ({
            ...val,
            key: idx + 1,
            created_at: new Date(val.created_at).toLocaleString(),
          }));
          setAgentGroupList(updatedList);
        } else {
          toast.error('Failed to fetch agent groups');
        }
      })
      .catch(() => toast.error('Error fetching agent groups'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const user = getUser();
    if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    fetchAgentGroups();
  }, []);

  const handleDeleteSelectedAgentGroups = () => {
    if (selectedAgentGroups.length === 0) return;
    postApiCall(API_AGENT_GROUP_DELETE, { agentGroupIds: selectedAgentGroups })
      .then(() => {
        toast.success('Agent groups deleted successfully');
        fetchAgentGroups();
        setSelectedAgentGroups([]);
      })
      .catch((err) => toast.error(`Unable to delete agent groups: ${err}`))
      .finally(() => setEnableDeleteModal(false));
  };

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'call-group-list',
        data: agentGroupList,
        columns: [
          { header: '#', accessor: 'key' },
          { header: 'Group Name', accessor: (row) => row.name || '-' },
          { header: 'Description', accessor: (row) => row.description || '-' },
          {
            header: 'Created At',
            accessor: (row) =>
              new Date(row.created_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const onCheckboxChange = (checked, groupId) =>
    setSelectedAgentGroups((prev) => (checked ? [...prev, groupId] : prev.filter((id) => id !== groupId)));

  const onHeaderCheckboxChange = (checked) => {
    if (checked) setSelectedAgentGroups(agentGroupList.map((g) => g.id));
    else setSelectedAgentGroups([]);
  };

  const handleEditAgentGroup = (groupId) =>
    navigate(`/group-settings/${groupId}`, { state: { groupId } });

  const canRead = hasPermission(moduleOperationsForReadPermissionCheck, 'Call Group', 'read');
  if (Object.keys(moduleOperationsForReadPermissionCheck).length > 0 && !canRead) {
    return (
      <div className="my-12 p-4 text-center text-base">No read permission found in assigned ACL</div>
    );
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      header: () => (
        <Checkbox
          checked={selectedAgentGroups.length === agentGroupList.length && agentGroupList.length > 0}
          onCheckedChange={onHeaderCheckboxChange}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedAgentGroups.includes(row.original.id)}
          onCheckedChange={(v) => onCheckboxChange(v, row.original.id)}
        />
      ),
    },
    { accessorKey: 'group_name', header: 'Group Name' },
    { id: 'member', header: 'Member', cell: ({ row }) => row.original.agents?.length || 0 },
    { id: 'regular_agent', header: 'Regular Agent', cell: ({ row }) => row.original.regular_agent_count ?? 0 },
    { id: 'team_lead_agent', header: 'Team Lead Agent', cell: ({ row }) => row.original.team_lead_agent_count ?? 0 },
    { accessorKey: 'strategy', header: 'Strategy' },
    {
      id: 'allocated_clis',
      header: 'Allocated CLIs',
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          title="View allocated CLIs"
          onClick={() => openViewClisModal(row.original)}
        >
          View CLIs
        </Button>
      ),
    },
    { accessorKey: 'answered', header: 'Answered' },
    { accessorKey: 'missed', header: 'Missed' },
    { id: 'sticky', header: 'Sticky', cell: ({ row }) => (row.original.sticky ? '✅' : '❌') },
    { id: 'time', header: 'Time', cell: ({ row }) => (row.original.group_schedule_id ? '✅' : '❌') },
    {
      accessorKey: 'updated_at',
      header: 'Updated At',
      cell: ({ row }) =>
        new Date(row.original.updated_at).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-1">
          {loggedInUserACL[MODULE_ACL.CALL_GROUP]?.edit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => handleEditAgentGroup(row.original.id)}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {loggedInUserACL[MODULE_ACL.CALL_GROUP]?.delete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive"
              onClick={() => {
                setSelectedAgentGroups((prev) =>
                  prev.includes(row.original.id) ? prev : [...prev, row.original.id],
                );
                setEnableDeleteModal(true);
              }}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Call Groups"
        description="Named agent groups used for routing, campaign fan-out, and skill-based dialling."
        actions={
          loggedInUserACL[MODULE_ACL.CALL_GROUP]?.add && (
            <Button onClick={() => navigate('/create-agent-group')}>
              <Plus className="h-4 w-4" />
              Create Call Group
            </Button>
          )
        }
      />
      <Card>
        <CardContent className="px-0">
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2 px-4">
                {loggedInUserACL?.[MODULE_ACL.CALL_GROUP]?.delete && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setEnableDeleteModal(true)}
                    disabled={selectedAgentGroups.length === 0}
                  >
                    Delete Selected
                  </Button>
                )}
                {agentGroupList.length !== 0 && (
                  <Button size="sm" onClick={handleExport} disabled={buttonDisable} className="gap-2">
                    Export Data
                    {buttonDisable && <Loader2 className="h-4 w-4 animate-spin" />}
                  </Button>
                )}
                <div className="relative ml-auto">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search..."
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    className="!pl-11 w-[240px]"
                    style={{ paddingLeft: '2.75rem' }}
                  />
                </div>
              </div>
              <div className="scroll-container">
                <DataTable
                  tableId="agent-groups"
                  columns={columns}
                  data={agentGroupList.filter((g) => {
                    if (!globalFilter) return true;
                    const q = globalFilter.toLowerCase();
                    return (
                      String(g.group_name || '').toLowerCase().includes(q) ||
                      String(g.strategy || '').toLowerCase().includes(q) ||
                      String(g.answered || '').toLowerCase().includes(q) ||
                      String(g.missed || '').toLowerCase().includes(q)
                    );
                  })}
                  pageSize={10}
                  emptyTitle="No call groups"
                  disableExport
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={enableDeleteModal}
        onOpenChange={setEnableDeleteModal}
        title="Confirm Delete"
        description="Are you sure you want to delete the selected agent group(s)?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={handleDeleteSelectedAgentGroups}
      />

      <Dialog open={showViewClisModal} onOpenChange={setShowViewClisModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Allocated CLIs
              {viewClisGroup?.group_name ? ` – ${viewClisGroup.group_name}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            {viewClisLoading ? (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : viewClisList.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">No CLIs allocated</div>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {viewClisList.map((n) => (
                  <li key={n.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span>{n.actual_number}</span>
                    {n.is_default && <Badge>Default</Badge>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default AgentGroups;
